import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { api } from "@/api";
import { useLightbox } from "@/components/LightboxProvider";
import { ZoomIn, Star } from "lucide-react";

export default function ProjectsGallery() {
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/projects")
      .then((r) => setProjects(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="py-24 sm:py-32 relative bg-black/40 border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="font-mono-accent text-xs uppercase tracking-[0.25em] text-brand-blue-light">
              {t.projects.eyebrow}
            </div>
            <h2 className="font-heading mt-3 text-4xl sm:text-5xl font-black tracking-tighter">
              {t.projects.title}
            </h2>
            <p className="mt-4 text-zinc-400">{t.projects.subtitle}</p>
          </div>
          <div className="font-mono-accent text-[11px] uppercase text-zinc-500 tracking-[0.2em]">
            {loading ? "— Loading —" : `${projects.length} pieces`}
          </div>
        </div>

        {!loading && projects.length === 0 ? (
          <div className="mt-12 border border-dashed border-white/10 p-12 text-center text-zinc-500 font-mono-accent uppercase text-xs">
            {t.projects.empty}
          </div>
        ) : (
          <div
            data-testid="projects-gallery"
            className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
          >
            {projects.map((p, i) => (
              <ProjectTile
                key={p.id}
                project={p}
                featuredLabel={t.featured}
                span={i % 6 === 0 ? "row-span-2 col-span-2" : ""}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ProjectTile({ project, span, featuredLabel }) {
  const { open } = useLightbox();
  const featured = !!project.featured;
  return (
    <button
      type="button"
      onClick={() => open(project.image_url, project.title)}
      data-testid={`project-tile-${project.id}`}
      className={`group relative overflow-hidden border ${featured ? "border-amber-300/70 shadow-[0_0_22px_rgba(251,191,36,0.25)]" : "border-white/5"} hover:border-brand-blue/50 transition-colors text-left cursor-zoom-in ${span}`}
    >
      <img
        src={project.image_url}
        alt={project.title}
        loading="lazy"
        className="w-full h-64 md:h-72 object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />
      {featured && (
        <div
          data-testid={`project-featured-badge-${project.id}`}
          className="absolute top-3 left-3 inline-flex items-center gap-1 font-mono-accent text-[10px] font-bold uppercase tracking-[0.2em] text-black bg-amber-300 border border-amber-200 px-2 py-1"
        >
          <Star size={10} fill="currentColor" />
          {featuredLabel}
        </div>
      )}
      <div className="absolute top-3 right-3 bg-black/70 border border-white/10 group-hover:border-brand-blue/60 p-1.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
        <ZoomIn size={14} className="text-brand-blue-light" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-brand-blue-light">
          {project.category.replace(/_/g, " ")}
        </div>
        <div className="font-heading text-lg font-extrabold mt-1">{project.title}</div>
        <div className="text-xs text-zinc-300 mt-1 line-clamp-2">{project.description}</div>
      </div>
    </button>
  );
}
